import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Settings, Clock, Mail, ArrowRight, Shield } from "lucide-react";

// Use the same logo as the header
const LOGO_URL = "/amidex-logo.png";

const DEFAULT_MESSAGE = "We're upgrading performance and reliability. Please check back soon.";

const MaintenancePage = ({ customMessage }) => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  
  const displayMessage = customMessage || DEFAULT_MESSAGE;

  const handleNotifySubmit = (e) => {
    e.preventDefault();
    // For now, just show success state - no backend required
    if (email) {
      setSubmitted(true);
      // In future, this could POST to backend
    }
  };

  return (
    <>
      <Helmet>
        <title>Scheduled Maintenance | Amidex</title>
        <meta name="robots" content="noindex,nofollow" />
        <meta name="description" content="Amidex is currently undergoing scheduled maintenance. We'll be back soon with improved performance and reliability." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4 overflow-hidden relative">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Gradient orbs */}
          <div className="absolute top-1/4 -left-32 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-500/5 rounded-full blur-3xl" />
          
          {/* Grid pattern */}
          <div 
            className="absolute inset-0 opacity-[0.02]"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                               linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: '50px 50px'
            }}
          />
        </div>

        <div className="relative z-10 max-w-2xl w-full">
          {/* Logo/Brand - Centered between top and card */}
          <div className="flex items-center justify-center pt-8 md:pt-12 mb-16 md:mb-20">
            <img 
              src={LOGO_URL} 
              alt="Amidex" 
              className="h-16 md:h-20 lg:h-24 w-auto brightness-0 invert"
              data-testid="maintenance-logo"
            />
          </div>

          {/* Main Card */}
          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-3xl p-8 md:p-12 shadow-2xl">
            {/* Status Badge */}
            <div className="flex justify-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20">
                <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                <span className="text-amber-300 text-sm font-medium">System Maintenance</span>
              </div>
            </div>

            {/* Icon */}
            <div className="flex justify-center mb-8">
              <div 
                className="w-24 h-24 rounded-2xl bg-gradient-to-br from-blue-600/20 to-cyan-500/20 border border-blue-500/20 flex items-center justify-center transition-transform duration-500"
                style={{ transform: isHovered ? 'rotate(180deg)' : 'rotate(0deg)' }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <Settings className="w-12 h-12 text-blue-400" />
              </div>
            </div>

            {/* Content */}
            <div className="text-center mb-10">
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight">
                Scheduled Maintenance
              </h1>
              <p className="text-slate-400 text-lg leading-relaxed max-w-md mx-auto">
                {displayMessage}
              </p>
            </div>

            {/* Features being improved */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
              {[
                { icon: Shield, label: "Enhanced Security" },
                { icon: Clock, label: "Faster Performance" },
                { icon: Settings, label: "New Features" }
              ].map(({ icon: Icon, label }) => (
                <div 
                  key={label}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/[0.02] border border-white/5"
                >
                  <Icon className="w-5 h-5 text-blue-400" />
                  <span className="text-slate-300 text-sm">{label}</span>
                </div>
              ))}
            </div>

            {/* Notify Form */}
            <div className="border-t border-white/10 pt-8">
              {!submitted ? (
                <form onSubmit={handleNotifySubmit} className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 transition-all"
                      data-testid="maintenance-email-input"
                    />
                  </div>
                  <button
                    type="submit"
                    className="px-6 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all flex items-center justify-center gap-2 group"
                    data-testid="maintenance-notify-btn"
                  >
                    Notify Me
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </form>
              ) : (
                <div className="text-center py-4">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/20">
                    <div className="w-2 h-2 rounded-full bg-green-400" />
                    <span className="text-green-300 text-sm">We&apos;ll notify you when we&apos;re back!</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer Links */}
          <div className="mt-8 text-center">
            <div className="flex items-center justify-center gap-6 text-sm">
              <a 
                href="/privacy" 
                className="text-slate-500 hover:text-slate-300 transition-colors"
                data-testid="maintenance-privacy-link"
              >
                Privacy Policy
              </a>
              <span className="text-slate-700">•</span>
              <a 
                href="/terms" 
                className="text-slate-500 hover:text-slate-300 transition-colors"
                data-testid="maintenance-terms-link"
              >
                Terms
              </a>
              <span className="text-slate-700">•</span>
              <a 
                href="mailto:support@amidex.org" 
                className="text-slate-500 hover:text-slate-300 transition-colors"
                data-testid="maintenance-contact-link"
              >
                Contact
              </a>
            </div>
            <p className="mt-4 text-slate-600 text-xs">
              © {new Date().getFullYear()} Amidex. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default MaintenancePage;
